import type { ShippingAddressDto } from '@montugroup/circuit-api-contracts';

/**
 * Extracts the order code before any split suffix.
 *
 * Order codes can have various formats:
 * - Original: MPRS123
 * - Next: MPRS123-1
 * - Split: MPRS123-2-S1.
 *
 * This function removes the split suffix (e.g., `-S1`, `-S2`) from order codes.
 * If the order code does not have a split suffix, it returns the original code.
 *
 * @param orderCode - The full order code.
 * @returns - The base order code without the split suffix.
 *
 * @example
 * getBaseOrderCode('MPRS123-2-S1'); // Returns 'MPRS123-2'
 * getBaseOrderCode('MPRS123');      // Returns 'MPRS123'
 * getBaseOrderCode('MPRS123-1');    // Returns 'MPRS123-1'
 */
export function getBaseOrderCode(orderCode: string): string {
  // Regex to match the split suffix (e.g., '-S1', '-S2')
  const splitSuffixPattern = /-S\d+$/;

  return orderCode?.replace(splitSuffixPattern, '');
}

/**
 * Formats the shipping address into a single string.
 *
 * @param shippingAddress - The shipping address object.
 * @returns - The formatted shipping address string.
 */
export function formatShippingAddress(shippingAddress: ShippingAddressDto): string {
  const { address1, address2, city, zip, province } = shippingAddress;
  const addressParts = [address1, address2, city, zip, province].filter((part) => part && part.trim() !== '');

  return addressParts.join(' ');
}

/**
 * Calculates a discounted price using integer math (currency-safe).
 *
 * @param price - Original price in major units (e.g. dollars)
 * @param discountPercent - Discount percentage (e.g. 50 for 50%)
 * @returns - Discounted price in major units
 */
export function calculateDiscountedPrice({ price, discountPercent }: { price: number; discountPercent?: number }) {
  if (price < 0) {
    return null;
  }

  if (!discountPercent) {
    return null;
  }

  if (discountPercent < 0 || discountPercent > 100) {
    return null;
  }

  // Convert to minor units (e.g. cents)
  const priceInCents = Math.round(price * 100);

  // Calculate discount in cents using integer math
  const discountedCents = Math.round((priceInCents * (100 - discountPercent)) / 100);

  // Convert back to major units
  return discountedCents / 100;
}

/**
 * Returns a whole number if the price ends in .00 otherwise returns both decimal digits eg: 7.5 becomes 7.50, and 8 remains as 8
 *
 * @param price - price eg: 7.5
 * @returns string - whole number if price would've ended in .00, otherwise returns both decimal digits eg: "7.50" or "8"
 */
export function formatPriceForBrevity(price: number) {
  return price.toFixed(2).replace(/\.00$/, '');
}
